import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, ArrowLeft, Building2, Zap, Shield, ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { locationData, referencesByLocation } from '../data/references';

function getCategoryIcon(category) {
  if (category.includes("Sicherheit")) return Shield;
  if (category.includes("Elektro") || category.includes("Klingel")) return Zap;
  return Building2;
}

function Lightbox({ images, currentIndex, onClose, onPrev, onNext, name }) {
  useEffect(function() {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    }
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return function() {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 text-white/80 hover:text-white z-50 p-2"
        aria-label="Schließen"
      >
        <X className="w-8 h-8" />
      </button>
      
      <button 
        onClick={function(e) { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full"
        aria-label="Vorheriges Bild"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      
      <button 
        onClick={function(e) { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full"
        aria-label="Nächstes Bild"
      >
        <ChevronRight className="w-8 h-8" />
      </button>
      
      <div className="max-w-6xl max-h-[90vh] px-4" onClick={function(e) { e.stopPropagation(); }}>
        <img 
          src={images[currentIndex]} 
          alt={name + " - Bild " + (currentIndex + 1)} 
          className="max-w-full max-h-[85vh] object-contain mx-auto"
        />
        <div className="text-center text-white/70 mt-4 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
}

function ImageGallery({ gallery, name, imagePosition, onImageClick }) {
  var images = [];
  var objectPos = imagePosition === "top" ? "object-top" : "object-cover";
  for (var i = 0; i < gallery.length; i++) {
    (function(index) {
      images.push(
        <div key={index} className="flex-shrink-0 w-full h-full snap-center cursor-pointer" onClick={function() { onImageClick(index); }}>
          <img src={gallery[index]} alt={name + " - Bild " + (index + 1)} className={"w-full h-full object-cover " + objectPos} />
        </div>
      );
    })(i);
  }
  return (
    <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-0 h-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {images}
    </div>
  );
}

function ReferenceCard({ name, category, description, services, subtitle, cardId, image, website, gallery, imagePosition }) {
  var Icon = getCategoryIcon(category);
  var hasGallery = gallery && gallery.length > 1;
  var objectPos = imagePosition === "top" ? "object-top" : "object-center";
  
  var lightboxState = useState(false);
  var isLightboxOpen = lightboxState[0];
  var setIsLightboxOpen = lightboxState[1];
  
  var indexState = useState(0);
  var currentImageIndex = indexState[0];
  var setCurrentImageIndex = indexState[1];
  
  var allImages = gallery || (image ? [image] : []);
  
  function openLightbox(index) {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  }
  
  function closeLightbox() {
    setIsLightboxOpen(false);
  }
  
  function nextImage() {
    setCurrentImageIndex(function(prev) { return (prev + 1) % allImages.length; });
  }
  
  function prevImage() {
    setCurrentImageIndex(function(prev) { return (prev - 1 + allImages.length) % allImages.length; });
  }
  
  return (
    <div data-testid={cardId} className="bg-white border border-zinc-200 overflow-hidden group hover:border-red-200 transition-all duration-300">
      <div className="aspect-video bg-zinc-100 relative overflow-hidden">
        {hasGallery ? (
          <div className="w-full h-full relative">
            <ImageGallery gallery={gallery} name={name} imagePosition={imagePosition} onImageClick={openLightbox} />
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">
              {gallery.length} Bilder - Klicken zum Vergrößern
            </div>
          </div>
        ) : image ? (
          <img 
            src={image} 
            alt={name} 
            className={"w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer " + objectPos} 
            onClick={function() { openLightbox(0); }}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400">
            <Icon className="w-12 h-12 mb-3 text-zinc-300" strokeWidth={1} />
            <span className="text-sm font-medium">Bild folgt</span>
          </div>
        )}
        <div className="absolute top-4 left-4">
          <span className="bg-[#E31E24] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">{category}</span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-neutral-900 mb-1 group-hover:text-[#E31E24] transition-colors">{name}</h3>
        {subtitle ? <p className="text-sm text-[#E31E24] font-medium mb-3">{subtitle}</p> : null}
        <p className="text-neutral-600 text-sm leading-relaxed mb-5">{description}</p>
        <ServiceTags items={services} />
        {website ? (
          <a href={website} target="_blank" rel="noopener noreferrer" data-testid={"website-link-" + cardId} className="inline-flex items-center gap-2 text-[#E31E24] font-semibold text-sm mt-4 hover:underline">
            <ExternalLink className="w-4 h-4" />
            Website besuchen
          </a>
        ) : null}
      </div>
      
      {isLightboxOpen && allImages.length > 0 ? (
        <Lightbox 
          images={allImages} 
          currentIndex={currentImageIndex} 
          onClose={closeLightbox} 
          onPrev={prevImage} 
          onNext={nextImage}
          name={name}
        />
      ) : null}
    </div>
  );
}

function ServiceTags({ items }) {
  var tags = [];
  for (var i = 0; i < items.length; i++) {
    tags.push(
      <span key={items[i]} className="text-xs font-medium bg-zinc-100 text-neutral-700 px-3 py-1.5 border border-zinc-200">{items[i]}</span>
    );
  }
  return <div className="flex flex-wrap gap-2">{tags}</div>;
}

function LocationFilters({ activeId, onChange }) {
  var buttons = [];
  for (var i = 0; i < locationData.length; i++) {
    var loc = locationData[i];
    var isActive = loc.id === activeId;
    var refs = referencesByLocation[loc.id];
    var count = refs ? refs.length : 0;
    buttons.push(
      <LocationButton key={loc.id} locId={loc.id} locName={loc.name} isActive={isActive} count={count} onChange={onChange} />
    );
  }
  return (
    <section className="bg-white border-b border-zinc-200 sticky top-[72px] z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 overflow-x-auto py-3 -mx-4 px-4 sm:mx-0 sm:px-0">{buttons}</div>
      </div>
    </section>
  );
}

function LocationButton({ locId, locName, isActive, count, onChange }) {
  return (
    <button
      onClick={function() { onChange(locId); }}
      data-testid={"filter-" + locId}
      className={"flex items-center gap-2 px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-all duration-200 border " + (isActive ? "bg-[#E31E24] text-white border-[#E31E24]" : "bg-white text-neutral-700 border-zinc-200 hover:border-[#E31E24] hover:text-[#E31E24]")}
    >
      <MapPin className="w-4 h-4" />
      {locName}
      <span className={"text-xs px-1.5 py-0.5 " + (isActive ? "bg-white/20" : "bg-zinc-100")}>{count}</span>
    </button>
  );
}

function ReferenceGrid({ locationId }) {
  var refs = referencesByLocation[locationId];
  if (!refs || refs.length === 0) {
    return (
      <div className="text-center py-20 text-neutral-400">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
        <p className="text-lg font-medium">Noch keine Projekte.</p>
      </div>
    );
  }
  var cards = [];
  for (var i = 0; i < refs.length; i++) {
    var r = refs[i];
    cards.push(
      <ReferenceCard
        key={r.id}
        cardId={"reference-card-" + r.id}
        name={r.name}
        category={r.category}
        description={r.description}
        services={r.services}
        subtitle={r.subtitle || null}
        image={r.image || null}
        website={r.website || null}
        gallery={r.gallery || null}
        imagePosition={r.imagePosition || null}
      />
    );
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>;
}

export default function ReferenzenPage() {
  var params = useParams();
  var navigate = useNavigate();
  var locationId = params.locationId || "wuppertal";

  useEffect(function() { window.scrollTo(0, 0); }, []);

  function handleLocationChange(locId) {
    navigate("/referenzen/" + locId);
  }

  var activeLocation = locationId;

  var currentLocationName = "Wuppertal";
  for (var i = 0; i < locationData.length; i++) {
    if (locationData[i].id === activeLocation) {
      currentLocationName = locationData[i].name;
      break;
    }
  }
  var refCount = 0;
  var refsArr = referencesByLocation[activeLocation];
  if (refsArr) { refCount = refsArr.length; }

  return (
    <div className="min-h-screen" data-testid="referenzen-page">
      <SEO page="referenzen" />
      <Header />
      <main>
        <section className="bg-neutral-900 pt-28 pb-16 md:pt-36 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/#referenzen" data-testid="back-to-home" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Startseite
            </Link>
            <span className="block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">Referenzen</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Unsere Projekte</h1>
            <p className="text-white/60 text-base lg:text-lg max-w-2xl">Wählen Sie einen Standort, um die dort realisierten Projekte zu sehen.</p>
          </div>
        </section>

        <LocationFilters activeId={activeLocation} onChange={handleLocationChange} />

        <section className="py-16 md:py-24 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E31E24] flex items-center justify-center text-white flex-shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">Projekte in {currentLocationName}</h2>
                <p className="text-sm text-neutral-500">{refCount} {refCount === 1 ? "Projekt" : "Projekte"}</p>
              </div>
            </div>
            <ReferenceGrid locationId={activeLocation} />
          </div>
        </section>

        <section className="bg-neutral-900 py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ihr Projekt ist unser nächstes Referenzprojekt</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Kontaktieren Sie uns für eine kostenlose Beratung.</p>
            <Link to="/#kontakt" data-testid="referenzen-cta-contact" className="btn-primary inline-block">Jetzt Anfrage stellen</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
